import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Chip, CircularProgress, TextField, MenuItem, Button,
  Tooltip, IconButton,
} from "@mui/material";
import RefreshIcon    from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import WarningIcon      from "@mui/icons-material/Warning";
import api from "../../utils/api";

const STATUS_CONFIG = {
  sent:     { label:"Sent",     color:"#16a34a", bg:"rgba(34,197,94,0.1)",   icon:<CheckCircleIcon sx={{fontSize:14}} /> },
  bounced:  { label:"Bounced",  color:"#dc2626", bg:"rgba(220,38,38,0.1)",   icon:<ErrorOutlineIcon sx={{fontSize:14}} /> },
  rejected: { label:"Rejected", color:"#b45309", bg:"rgba(180,83,9,0.1)",    icon:<WarningIcon sx={{fontSize:14}} /> },
  error:    { label:"Error",    color:"#7c3aed", bg:"rgba(124,58,237,0.1)",  icon:<ErrorOutlineIcon sx={{fontSize:14}} /> },
};

const fmtDT = (d) => d ? new Date(d).toLocaleString() : "N/A";

const EmailDeliveryLog = () => {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [status,  setStatus]  = useState("");
  const [mod,     setMod]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit:200 });
      if (status) params.set("status", status);
      if (mod)    params.set("module", mod);
      const res = await api.get(`/email-logs?${params}`);
      setLogs(res.data || []);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [status, mod]);

  useEffect(() => { load(); }, [load]);

  const bounced  = logs.filter(l => l.status === "bounced"  || l.status === "rejected");
  const sent     = logs.filter(l => l.status === "sent");

  const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" }, minWidth:130 };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Email Delivery Log</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Track outbound emails and identify bounce / delivery errors.
          </Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Summary chips */}
      <Box sx={{ display:"flex", gap:2, mb:3, flexWrap:"wrap" }}>
        {[
          { label:`${logs.length} Total`, color:"#2f63ad", bg:"rgba(47,99,173,0.08)" },
          { label:`${sent.length} Delivered`, color:"#16a34a", bg:"rgba(34,197,94,0.08)" },
          { label:`${bounced.length} Bounced / Rejected`, color:"#dc2626", bg:"rgba(220,38,38,0.08)" },
        ].map(({ label, color, bg }) => (
          <Chip key={label} label={label} sx={{ fontWeight:700, bgcolor:bg, color, borderRadius:"10px" }} />
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display:"flex", gap:2, mb:2.5, flexWrap:"wrap" }}>
        <TextField select size="small" label="Status" value={status}
          onChange={e => setStatus(e.target.value)} sx={fieldSx}>
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="bounced">Bounced</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField select size="small" label="Module" value={mod}
          onChange={e => setMod(e.target.value)} sx={fieldSx}>
          <MenuItem value="">All modules</MenuItem>
          <MenuItem value="violations">Violations</MenuItem>
          <MenuItem value="workorders">Work Orders</MenuItem>
          <MenuItem value="arch">Architectural</MenuItem>
        </TextField>
        <Button variant="outlined" size="small" onClick={() => { setStatus(""); setMod(""); }}
          sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>
          Reset
        </Button>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        {loading ? (
          <Box sx={{ display:"flex", justifyContent:"center", py:4 }}><CircularProgress /></Box>
        ) : logs.length === 0 ? (
          <Typography sx={{ textAlign:"center", py:4, color:"#6b778c" }}>No email logs found.</Typography>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
              <TableRow>
                {["Status","Recipient","Subject","Module","Sent At","Bounce / Error"].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => {
                const sc = STATUS_CONFIG[log.status] || STATUS_CONFIG.sent;
                const module = log.violation_id ? "Violation"
                             : log.work_order_id ? "Work Order"
                             : log.architectural_request_id ? "Arch. Request"
                             : "System";
                return (
                  <TableRow key={log.id} hover sx={{ bgcolor: log.status !== "sent" ? "rgba(220,38,38,0.02)" : "inherit" }}>
                    <TableCell>
                      <Chip size="small" icon={sc.icon}
                        label={sc.label}
                        sx={{ fontWeight:700, fontSize:11, borderRadius:"8px",
                          bgcolor:sc.bg, color:sc.color, "& .MuiChip-icon":{ color:sc.color } }} />
                    </TableCell>
                    <TableCell sx={{ fontSize:12, color:"#223142", maxWidth:180 }}>
                      <Tooltip title={log.email_address}>
                        <Typography variant="caption" noWrap sx={{ display:"block", maxWidth:160 }}>
                          {log.email_address}
                        </Typography>
                      </Tooltip>
                      {log.rejected_recipients && (
                        <Typography variant="caption" sx={{ color:"#dc2626", display:"block", fontSize:10 }}>
                          Rejected: {log.rejected_recipients}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize:12, color:"#374151", maxWidth:220 }}>
                      <Tooltip title={log.subject}>
                        <Typography variant="caption" noWrap sx={{ display:"block", maxWidth:200 }}>
                          {log.subject}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={module} sx={{ fontSize:10, height:18,
                        bgcolor:"rgba(47,99,173,0.08)", color:"#2f63ad", borderRadius:"6px" }} />
                    </TableCell>
                    <TableCell sx={{ fontSize:11, color:"#6b778c", whiteSpace:"nowrap" }}>
                      {fmtDT(log.sent_at)}
                    </TableCell>
                    <TableCell sx={{ maxWidth:220 }}>
                      {log.error_message ? (
                        <Tooltip title={log.error_message}>
                          <Typography variant="caption" sx={{ color:"#dc2626", cursor:"help",
                            display:"block", maxWidth:200, fontSize:11 }} noWrap>
                            {log.error_message}
                          </Typography>
                        </Tooltip>
                      ) : log.status === "sent" ? (
                        <Typography variant="caption" sx={{ color:"#16a34a", fontSize:11 }}>
                          {log.accepted_recipients ? `Accepted ✓` : "Delivered"}
                        </Typography>
                      ) : null}
                      {log.message_id && (
                        <Typography variant="caption" sx={{ color:"#9ca3af", fontSize:10, display:"block" }}>
                          ID: {log.message_id.slice(0,30)}...
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {bounced.length > 0 && (
        <Box sx={{ mt:2, p:2, borderRadius:"12px", bgcolor:"rgba(220,38,38,0.04)", border:"1px solid rgba(220,38,38,0.15)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#dc2626", mb:0.5 }}>
            {bounced.length} bounce{bounced.length!==1?"s":""} detected
          </Typography>
          <Typography variant="caption" sx={{ color:"#6b778c" }}>
            Bounces happen when the recipient's email server rejects the message - usually due to an invalid address,
            full inbox, or the sending domain being blocked. Check the error message for details and verify
            the recipient's email address is correct.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EmailDeliveryLog;
